import { useState, ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle,
  Search,
  User,
  Mail,
  Play,
  X,
  Loader2,
  Megaphone,
} from 'lucide-react'
import { companiesApi } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

interface BulkPipelineToolbarProps {
  selectedIds: Set<string>
  onClear: () => void
  onActionComplete?: () => void
  invalidateQueries?: string[][]
  className?: string
  children?: ReactNode
}

type PipelineStep = 'qualify' | 'analyze' | 'contact' | 'outreach'

const STEP_ACTIONS: {
  step: PipelineStep
  label: string
  icon: typeof CheckCircle
}[] = [
  { step: 'qualify', label: 'Qualify', icon: CheckCircle },
  { step: 'analyze', label: 'Analyze', icon: Search },
  { step: 'contact', label: 'Find Contacts', icon: User },
  { step: 'outreach', label: 'Generate Outreach', icon: Mail },
]

export function BulkPipelineToolbar({
  selectedIds,
  onClear,
  onActionComplete,
  invalidateQueries = [['companies'], ['company-stage-counts']],
  className,
  children,
}: BulkPipelineToolbarProps) {
  const queryClient = useQueryClient()
  const [autoSendOutreach, setAutoSendOutreach] = useState(false)
  const [activeStep, setActiveStep] = useState<PipelineStep | 'full' | null>(null)

  const runPipelineMutation = useMutation({
    mutationFn: ({
      step,
      sendEmail,
    }: {
      step?: PipelineStep
      sendEmail?: boolean
    }) => companiesApi.runPipeline(Array.from(selectedIds), { step, sendEmail }),
    onSuccess: () => {
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })
      onActionComplete?.()
      onClear()
      setActiveStep(null)
    },
    onError: () => {
      setActiveStep(null)
    },
  })

  const handleStepAction = (step: PipelineStep) => {
    setActiveStep(step)
    runPipelineMutation.mutate({ step })
  }

  const handleFullPipeline = () => {
    setActiveStep('full')
    runPipelineMutation.mutate({ sendEmail: autoSendOutreach })
  }

  if (selectedIds.size === 0) {
    return null
  }

  const isLoading = runPipelineMutation.isPending
  const count = selectedIds.size

  return (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
        'bg-background border rounded-lg shadow-lg p-3',
        'flex items-center gap-3 flex-wrap justify-center',
        className
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
          {count}
        </span>
        <span className="text-muted-foreground">
          {count === 1 ? 'company' : 'companies'} selected
        </span>
      </div>

      <div className="h-6 w-px bg-border" />

      <div className="flex items-center gap-1.5">
        {STEP_ACTIONS.map(({ step, label, icon: Icon }) => (
          <Button
            key={step}
            variant="outline"
            size="sm"
            onClick={() => handleStepAction(step)}
            disabled={isLoading}
            className="h-8"
          >
            {isLoading && activeStep === step ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Icon className="h-3.5 w-3.5 mr-1.5" />
            )}
            {label}
          </Button>
        ))}
      </div>

      <div className="h-6 w-px bg-border" />

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={autoSendOutreach}
            onCheckedChange={(checked) => setAutoSendOutreach(checked === true)}
            disabled={isLoading}
          />
          Auto send
        </label>
        <Button
          variant="default"
          size="sm"
          onClick={handleFullPipeline}
          disabled={isLoading}
          className="h-8"
        >
          {isLoading && activeStep === 'full' ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5 mr-1.5" />
          )}
          Run Full Pipeline
        </Button>
      </div>

      {children && (
        <>
          <div className="h-6 w-px bg-border" />
          {children}
        </>
      )}

      <div className="h-6 w-px bg-border" />

      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        disabled={isLoading}
        className="h-8 px-2"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}

interface BulkAddToCampaignButtonProps {
  selectedIds: Set<string>
  onSelect: () => void
  disabled?: boolean
}

export function BulkAddToCampaignButton({
  selectedIds,
  onSelect,
  disabled,
}: BulkAddToCampaignButtonProps) {
  if (selectedIds.size === 0) {
    return null
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onSelect}
      disabled={disabled}
      className="h-8"
    >
      <Megaphone className="h-3.5 w-3.5 mr-1.5" />
      Add to Campaign
    </Button>
  )
}
